import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { API } from "@/lib/api";
import { eur } from "@/lib/product";
import { useAdsConsent } from "@/lib/consent";
import { purchaseOnce } from "@/lib/metaPixel";

export default function OrderConfirmed() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(sessionId ? "checking" : "error");
  const [data, setData] = useState(null);
  const ads = useAdsConsent();

  useEffect(() => {
    document.title = "Commande confirmée | ClimNova";
    if (!sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data: d } = await axios.get(`${API}/orders/status/${sessionId}`);
        setData(d);
        if (d.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(d.payment_status)) return setState("error");
        if (d.payment_status === "processing") setState("processing");
      } catch {
        if (tries > 3) return setState("error");
      }
      if (tries >= 30) return setState((s) => (s === "processing" ? "processing" : "timeout"));
      timer = setTimeout(poll, tries > 10 ? 5000 : 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  useEffect(() => {
    if (state === "paid" && ads && data?.order_id) purchaseOnce(data.order_id, data.amount_paid ?? data.total, data.quantity);
  }, [state, ads, data]);

  return (
    <div data-testid="order-confirmed-page">
      <Header />
      <main className="bg-ice-soft min-h-[70vh] flex items-center">
        <div className="container-x max-w-2xl py-16">
          <div className="order-card p-8 sm:p-12 flex flex-col items-center text-center gap-5">
            {state === "checking" && (
              <>
                <Loader2 data-testid="payment-checking" className="w-12 h-12 text-sky-600 animate-spin" />
                <h1 className="font-display text-3xl font-extrabold text-slate-900">Vérification du paiement…</h1>
                <p className="text-slate-600">Merci de patienter quelques secondes.</p>
              </>
            )}
            {state === "paid" && (
              <>
                <CheckCircle2 className="w-14 h-14 text-emerald-600" />
                <h1 data-testid="payment-success-title" className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Merci, votre commande est confirmée !</h1>
                <p className="text-slate-600">
                  Commande <strong data-testid="confirmed-order-id">{data.order_id}</strong> — {data.quantity} × Midea PortaSplit — total payé{" "}
                  <strong data-testid="confirmed-order-total">{eur(data.total)}</strong>.
                </p>
                <p className="text-slate-600">Un e-mail de confirmation vous a été envoyé. Vous disposez d'un délai de rétractation de 14 jours à réception.</p>
              </>
            )}
            {state === "processing" && (
              <>
                <Clock className="w-12 h-12 text-sky-600" />
                <h1 data-testid="payment-processing-title" className="font-display text-3xl font-extrabold text-slate-900">Paiement en cours de validation</h1>
                <p className="text-slate-600">Votre commande {data?.order_id} est bien enregistrée. Le paiement est en cours de validation par l'organisme de paiement ; cette page se met à jour automatiquement et vous recevrez un e-mail de confirmation dès validation.</p>
              </>
            )}
            {state === "timeout" && (
              <>
                <Clock className="w-12 h-12 text-orange-600" />
                <h1 data-testid="payment-pending-title" className="font-display text-3xl font-extrabold text-slate-900">Paiement en cours de traitement</h1>
                <p className="text-slate-600">Nous n'avons pas encore reçu la confirmation de votre banque. Vous recevrez un e-mail dès validation.</p>
              </>
            )}
            {state === "error" && (
              <>
                <Clock className="w-12 h-12 text-orange-600" />
                <h1 data-testid="payment-error-title" className="font-display text-3xl font-extrabold text-slate-900">Paiement non confirmé</h1>
                <p className="text-slate-600">Nous n'avons pas pu confirmer votre paiement. Aucun montant n'a été débité si le paiement n'a pas abouti.</p>
              </>
            )}
            <Link to="/" data-testid="confirmed-back-home" className="btn-heat mt-2">Retour au site</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
